import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { KubernetesAccount } from 'src/app/cc-api/models';
import { UiAccountsControllerService } from 'src/app/cc-api/services';
import { whiteSpaceInputPattern } from 'src/app/constants/inputPatterns';
import { CustomToastService } from 'src/app/pages/custom-toast/custom-toast.service';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import { getToastSubTexts } from 'src/app/utils/toast.utils';

@Component({
  selector: 'app-kubernetes-account-create-edit',
  templateUrl: './kubernetes-account-create-edit.component.html',
  styleUrls: ['./kubernetes-account-create-edit.component.scss'],
})
export class KubernetesAccountCreateEditComponent implements OnChanges {
  @Input() cardWidth: string = '35vw';
  @Input() mode: string = '';
  @Input() passRef: any;
  @Output() afterSaveAccount: EventEmitter<any> = new EventEmitter<any>();
  @Input() kubernetesAccountData: any;
  createUpdateInProgress: boolean = false;
  verifyInProgress: boolean = false;
  accountDetails: KubernetesAccount;
  kubernetesAccountForm: UntypedFormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.pattern('^[a-z0-9-]+$')]],
    host: [
      '',
      [Validators.required, Validators.pattern(whiteSpaceInputPattern)],
    ],
    certificateAuthority: [
      '',
      [Validators.required, Validators.pattern(whiteSpaceInputPattern)],
    ],
    token: [
      '',
      [Validators.required, Validators.pattern(whiteSpaceInputPattern)],
    ],
  });
  readonly ACCOUNTS_WRITE = 'ACCOUNTS_WRITE';

  constructor(
    private fb: UntypedFormBuilder,
    private accountControllerService: UiAccountsControllerService,
    private customToast: CustomToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.kubernetesAccountData?.currentValue) {
      this.accountDetails = changes.kubernetesAccountData?.currentValue;
      this.kubernetesAccountForm.patchValue({
        name: this.accountDetails?.name || '',
        host: this.accountDetails?.host || '',
        certificateAuthority: this.accountDetails?.certificateAuthority || '',
        token: this.accountDetails?.token || '',
      });
      this.kubernetesAccountForm.patchValue(
        changes.kubernetesAccountData?.currentValue,
      );
    }
  }

  createEditKubernetesAccount() {
    this.createUpdateInProgress = true;
    const isCreateMode = this.mode === 'create';
    let accountName =
      this.kubernetesAccountForm?.controls?.name?.value?.trim() || '';
    let host = this.kubernetesAccountForm?.controls?.host?.value?.trim() || '';
    let certificateAuthority =
      this.kubernetesAccountForm?.controls?.certificateAuthority?.value?.trim() ||
      '';
    let token =
      this.kubernetesAccountForm?.controls?.token?.value?.trim() || '';

    let accountParams: KubernetesAccount = {
      name: accountName,
      accountType: 'CLOUD',
      provider: 'KUBERNETES',
      host: host,
      certificateAuthority,
      token,
    };

    let httpReq;
    if (isCreateMode) {
      httpReq =
        this.accountControllerService.createKubernetesAccountUsingPOST(
          accountParams,
        );
    } else if (this.mode === 'edit') {
      httpReq = this.accountControllerService.updateKubernetesAccountUsingPUT({
        id: this.accountDetails?.id,
        k8sAccountRequest: {
          ...accountParams,
          id: this.accountDetails?.id,
          createdBy: this.accountDetails?.createdBy,
          creationDate: this.accountDetails?.creationDate,
        },
      });
    }
    let { successSubText, failureSubText } = getToastSubTexts(this.mode);
    httpReq.subscribe(
      (data) => {
        this.customToast.success(
          `Kubernetes account ${data?.name} ${successSubText} successfully`,
        );
        this.afterSaveAccount.emit(data);
        triggerAnalyticsEvent(
          `ORG_SETTINGS_ACCOUNT_MANAGEMENT_${this.mode?.toUpperCase()}_SUCCESSFUL`,
          {
            accountName: accountName,
            accountType: accountParams.accountType,
          },
        );
        this.passRef.close();
        this.createUpdateInProgress = false;
        this.kubernetesAccountForm.reset();
      },
      (err) => {
        this.createUpdateInProgress = false;
        this.customToast.error(
          err?.error?.message ||
            ` Failed to ${failureSubText} kubernetes account`,
        );
        triggerAnalyticsEvent(
          `ORG_SETTINGS_ACCOUNT_MANAGEMENT_${this.mode?.toUpperCase()}_ERROR`,
        );
      },
    );
  }
  get host() {
    return this.kubernetesAccountForm.get('host');
  }
  get certificateAuthority() {
    return this.kubernetesAccountForm.get('certificateAuthority');
  }
  get token() {
    return this.kubernetesAccountForm.get('token');
  }
  get name() {
    return this.kubernetesAccountForm.get('name');
  }
  verifyAccount() {
    triggerAnalyticsEvent(
      `ORG_SETTINGS_ACCOUNT_MANAGEMENT_${this.mode?.toUpperCase()}_VERIFY_ACCOUNT`,
    );
    this.verifyInProgress = true;
    const { host, certificateAuthority, token, name } =
      this.kubernetesAccountForm?.controls || {};
    let kubernetesAccountDetails: KubernetesAccount = {
      host: host?.value?.trim(),
      name: name?.value?.trim(),
      certificateAuthority: certificateAuthority?.value,
      token: token?.value?.trim(),
      accountType: 'CLOUD',
      provider: 'KUBERNETES',
    };
    this.accountControllerService
      .validateKubernetesAccountUsingPOST(kubernetesAccountDetails)
      .subscribe(
        (data) => {
          this.verifyInProgress = false;
          this.customToast.success(`This is a verified account`);
        },
        (error) => {
          this.verifyInProgress = false;
          this.customToast.error(
            error?.error?.message || `Failed to verify account`,
          );
        },
      );
  }

  onClickHandleCancel(passRef: any) {
    triggerAnalyticsEvent(
      `ORG_SETTINGS_ACCOUNT_MANAGEMENT_${this.mode?.toUpperCase()}_CANCEL`,
    );
    passRef.close();
  }
}
